import { Injectable, inject } from '@angular/core';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, combineLatest, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, take } from 'rxjs/operators';
import { MapDataLayerApi } from '../api/map-data-layer.api';
import * as MapDataLayersActions from './map-data-layers.actions';
import * as fromMapDataLayers from './map-data-layers.reducer';
import * as MapDataLayersSelectors from './map-data-layers.selectors';

@Injectable()
export class MapDataLayersEffects {
    private readonly actions$ = inject(Actions);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly mapDataLayerApi = inject(MapDataLayerApi);
    private readonly store$ = inject<Store<fromMapDataLayers.State>>(Store);

    /**
     * Effect for loading map data layers
     */
    public readonly initLoadMapDataLayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MapDataLayersActions.initLoadOfMapDataLayers),
            this.licenseFacade.hasLicenseFilter(License.NavtorDataLayers),
            concatMap(() =>
                combineLatest([
                    this.store$.pipe(select(MapDataLayersSelectors.mapDataLayersIsLoaded)),
                    this.store$.pipe(select(MapDataLayersSelectors.mapDataLayersIsLoading)),
                ]).pipe(
                    take(1),
                    // If it is loading then do nothing, else trigger load map layer config
                    mergeMap(([isLoaded, isLoading]) =>
                        isLoaded || isLoading ? EMPTY : of(MapDataLayersActions.loadMapDataLayers())
                    )
                )
            )
        )
    );

    public readonly initialize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.loadAppData),
            mergeMap(() => [MapDataLayersActions.initLoadOfMapDataLayers()])
        )
    );

    public readonly loadMapDataLayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MapDataLayersActions.loadMapDataLayers),
            this.licenseFacade.hasLicenseFilter(License.NavtorDataLayers),
            mergeMap(() =>
                this.mapDataLayerApi.getLegacyMapDataLayers().pipe(
                    map((dataLayers) =>
                        MapDataLayersActions.loadMapDataLayersSuccess({
                            dataLayers,
                        })
                    ),
                    catchError((error) =>
                        of(
                            MapDataLayersActions.loadMapDataLayersFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );
}
